// The OPFS SAH-pool VFS allows one connection per origin, so only one tab may hold the database.
// The holder keeps the `rf-db` Web Lock; another tab can ask it to let go via BroadcastChannel.

const LOCK_NAME = 'rf-db';
const CHANNEL_NAME = 'rf-db';

export interface DbLock {
  release(): void;
}

function hold(onAcquired: (lock: DbLock) => void, options: LockOptions): Promise<boolean> {
  return new Promise((resolveAttempt) => {
    navigator.locks
      .request(LOCK_NAME, options, (lock) => {
        if (!lock) {
          resolveAttempt(false);
          return undefined;
        }
        return new Promise<void>((releaseLock) => {
          resolveAttempt(true);
          onAcquired({ release: () => releaseLock() });
        });
      })
      .catch(() => resolveAttempt(false));
  });
}

/** Take the lock if nobody holds it. Resolves to null when another tab has the database open. */
export async function tryAcquireDbLock(): Promise<DbLock | null> {
  let acquired: DbLock | null = null;
  const ok = await hold((lock) => (acquired = lock), { ifAvailable: true });
  return ok ? acquired : null;
}

/** Ask the current holder to release, then wait for the lock. */
export async function takeOverDbLock(): Promise<DbLock> {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: 'release-request' });
  channel.close();
  let acquired: DbLock | null = null;
  await hold((lock) => (acquired = lock), {});
  return acquired!;
}

/** Called by the holder: runs `onRequested` when another tab asks for the database. */
export function listenForReleaseRequests(onRequested: () => void): () => void {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event) => {
    if (event.data?.type === 'release-request') onRequested();
  };
  return () => channel.close();
}

export function locksSupported() {
  return typeof navigator !== 'undefined' && 'locks' in navigator && typeof BroadcastChannel !== 'undefined';
}
